import { useRouter } from 'next/router'
import { VFC } from 'react'
import { UserIcon } from 'src/components/Image/UserIcon'
import { SmallButton } from 'src/elements/Button'
import { Link } from 'src/elements/Link'
import { EMPTY_NOTIFICATION, Notification } from 'src/models/Notification'
import { LoginUser } from 'src/models/User'
import { isPaidMember } from 'src/models/User/role'
import { AuthService } from 'src/services/AuthService'
import {
  _menuborder,
  _menuhover,
  _menuorange,
  cautionRed,
  facebookBlue,
  signoutGray,
  truewhite,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { getFirstDayOfNextMonth } from 'src/utils/dateUtils'
import {
  AUTH_FACEBOOK,
  CONTACT,
  EDIT_NEEDS,
  EDIT_PERSONA,
  EDIT_PROFILE,
  EDIT_SCHEDULE_URL,
  EDIT_SOLUTIONS,
  NOTICES,
  NOTICE_CONFIGURATION,
  REFERRER_MESSAGE_GROUP,
  TOP,
} from 'src/utils/routes'
import styled, { css } from 'styled-components'

export const DrawerMenu: VFC<{
  isOpen: boolean
  user: LoginUser
  messageSendableCount: Notification['messageSendableCount']
}> = ({
  isOpen,
  user: {
    plan: { paid, free, isReferrer, role },
    settings: { facebookLogin },
    profile: { name, iconSrc },
    company: { name: companyName, isActiveScheduleUrl },
    member,
  },
  messageSendableCount: { count } = EMPTY_NOTIFICATION.messageSendableCount,
}) => {
  const messageTotal = member?.messageTotal
  const router = useRouter()

  return (
    <>
      <MenuNav isOpen={isOpen}>
        <MenuSectionDiv>
          <ProfileDiv>
            <UserIcon src={iconSrc} size="60px" />
            <UserInfoDiv>
              <p>{companyName}</p>
              <p>{name}</p>
              {isPaidMember(paid, role) && <Role>有料会員</Role>}
            </UserInfoDiv>
          </ProfileDiv>
        </MenuSectionDiv>
        <MenuBody>
          <MenuLink href={EDIT_PROFILE}>プロフィールの編集</MenuLink>
          {/* Temporarily hidden - story list editing feature
          <MenuLink href={EDIT_STORYLIST}>ストーリー記事を編集</MenuLink>
          */}
        </MenuBody>
        <MenuBody>
          <MenuLink href={EDIT_NEEDS}>ニーズの編集</MenuLink>
          <MenuLink href={EDIT_SOLUTIONS}>ソリューションの編集</MenuLink>
          <MenuLink href={EDIT_PERSONA}>ペルソナ登録</MenuLink>
          {isActiveScheduleUrl && (
            <MenuLink href={EDIT_SCHEDULE_URL}>日程調整URL登録</MenuLink>
          )}
          <MenuLink href={NOTICES}>運営からのお知らせ</MenuLink>
          {/* <MenuLink href={USAGE_HISTORIES}>利用ステータス</MenuLink> */}
          <MenuLink href={NOTICE_CONFIGURATION}>通知設定</MenuLink>
          <MenuLink href={CONTACT}>お問い合わせ</MenuLink>
          {isReferrer && (
            <MenuLinkInhouse href={REFERRER_MESSAGE_GROUP}>
              【管理】メッセージグループ
            </MenuLinkInhouse>
          )}
        </MenuBody>
        {!free && (
          <MenuBody>
            <p>送信可能回数</p>
            {messageTotal === 99999 ? (
              <AvailableCount>
                <span>無制限</span>
              </AvailableCount>
            ) : (
              <>
                <AvailableCount>
                  残り <span>{count}</span> 通
                </AvailableCount>
                <NextCount>{getFirstDayOfNextMonth()}に追加予定</NextCount>
              </>
            )}
          </MenuBody>
        )}
        <MenuBody>
          <FacebookLink onClick={() => router.push(AUTH_FACEBOOK)}>
            {facebookLogin ? '連携解除' : 'Facebook連携'}
          </FacebookLink>
          <LogoutButton
            onClick={() =>
              AuthService.signOut().then(() => window.location.assign(TOP))
            }
          >
            ログアウトはこちら
          </LogoutButton>
        </MenuBody>
      </MenuNav>
    </>
  )
}
const MenuNav = styled.nav<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 40px);
  right: 80px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;

  width: 250px;
  padding: 14px;
  border-radius: 12px;

  font-weight: ${fontWeightBold};
  line-height: 1.4;

  background-color: ${truewhite};
  filter: drop-shadow(2px 4px 6px);

  opacity: 0;
  clip-path: inset(-10% -10% 100%);
  transition: all 1s;
  ${({ isOpen }) =>
    isOpen &&
    css`
      opacity: 1;
      clip-path: inset(-10%);
    `};
`

const MenuSectionDiv = styled.div`
  border-bottom: 1px solid ${_menuborder};
  width: 100%;
  padding-bottom: 20px;
  :last-child {
    border: none;
    padding-bottom: 10px;
  }
`

const MenuBody = styled(MenuSectionDiv)`
  display: flex;
  flex-direction: column;
  align-items: center;

  margin-top: 10px;
  > a,
  > button {
    margin-top: 10px;
  }
`
const UserInfoDiv = styled.div`
  margin-left: 10px;

  > * {
    :nth-child(2) {
      margin-top: 4px;
    }
    :nth-child(3) {
      margin-top: 6px;
    }
  }
`
const Role = styled.p`
  border-radius: 15px;
  padding: 2px 10px;
  background-color: ${_menuorange};
  color: ${truewhite};
  text-align: center;
`
const ProfileDiv = styled.div`
  display: flex;
`

const AvailableCount = styled.p`
  margin-top: 10px;
  span {
    font-size: 25px;
    color: ${_menuorange};
  }
`

const NextCount = styled.p`
  margin-top: 10px;
  font-size: 11px;
  font-weight: normal;
  color: #808080;
`

const MenuLink = styled(Link)`
  :hover,
  :focus {
    color: ${_menuhover};
    text-decoration: underline;
  }
`
const MenuLinkInhouse = styled(Link)`
  color: ${cautionRed};
  :hover,
  :focus {
    text-decoration: underline;
  }
`

const FacebookLink = styled(SmallButton)`
  width: 70%;
  background: ${facebookBlue};
  color: white;
`
const LogoutButton = styled(SmallButton)`
  width: 70%;
  background: ${signoutGray};
  color: white;
`
